import { isPossiblePhoneNumber as _isPossiblePhoneNumber, type CountryCode } from 'libphonenumber-js/min'
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator'
import { z } from 'zod/v4-mini'

export const isString = (value: unknown): boolean => {
  return typeof value === 'string'
}

export const isEmptyString = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  return value.trim() === ''
}

export const isNumber = (value: unknown): boolean => {
  return z.coerce.number().safeParse(value).success
}

export const isInteger = (value: unknown): boolean => {
  const { success, data } = z.coerce.number().safeParse(value)
  return success && Number.isInteger(data)
}

export const isNumberRange = (value: unknown, min?: number, max?: number): boolean => {
  const { success, data } = z.coerce.number().safeParse(value)
  if (!success) return false
  if (min && data < min) return false
  return !(max && data > max)
}

export const isPhoneNumber = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') return false
  const phoneNumber = value.toString()
  return /^\+?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(phoneNumber)
}

export const isPossiblePhoneNumber = (value: unknown, countryCode?: CountryCode) => {
  if (typeof value !== 'string' && typeof value !== 'number') return false
  const phoneNumber = value.toString()
  return _isPossiblePhoneNumber(phoneNumber, countryCode)
}

export const isEmail = (value: unknown): boolean => {
  return z.email().safeParse(value).success
}

export const normalizeEmail = (value: string): string => value.trim().toLowerCase()

export const isDomain = (value: string) => {
  return z.string().check(z.regex(z.regexes.domain)).safeParse(value).success
}

export const isPostalCode = (value: unknown, countryCode?: string): boolean => {
  if (typeof value !== 'string') return false
  if (!countryCode) return true
  if (!postcodeValidatorExistsForCountry(countryCode)) return true
  return postcodeValidator(value, countryCode)
}

// Subdomain only: at least three labels (e.g. mail.company.com); root domains are rejected.
export const isSenderSubdomain = (value: string): boolean =>
  /^[a-z0-9-]+\.[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(value.toLowerCase())

// Sanitize the sender local part: keep only ASCII alphanumerics and `. - _ +`.
export const sanitizeSenderLocalPart = (value: string): string => value.replace(/[^a-zA-Z0-9.\-_+]/g, '')

// Display name: required, max 64, printable ASCII + common Latin diacritics only. Mirrors
// business-service validation rules
export const isSenderDisplayName = (value: string): boolean => /^[\x20-\x7E\u00A0-\u024F]{1,64}$/.test(value)
